import { useEffect, useState } from "react";
import axios from "axios";
import useIsMobile from "../hooks/useIsMobile";

import API from "../config.js";

// ── STYLES ──────────────────────────────────────────────
const S = {
  wrap: {
    display: "flex", minHeight: "100vh",
    background: "#03050a", color: "#ddeeff",
    fontFamily: "'Exo 2', 'Rajdhani', sans-serif"
  },
  // SIDEBAR
  sidebar: {
    width: "240px", minHeight: "100vh", flexShrink: 0,
    background: "#07090f",
    borderRight: "1px solid #1a2438",
    display: "flex", flexDirection: "column",
    position: "sticky", top: 0, height: "100vh", overflowY: "auto"
  },
  sideTop: {
    padding: "24px 20px 20px",
    borderBottom: "1px solid #1a2438"
  },
  logo: {
    display: "flex", alignItems: "center", gap: "10px", marginBottom: "4px"
  },
  logoMark: {
    width: "38px", height: "38px",
    background: "linear-gradient(135deg,#00e5ff,#00b8d4)",
    borderRadius: "10px", display: "flex",
    alignItems: "center", justifyContent: "center",
    fontWeight: 900, fontSize: "14px", color: "#000",
    boxShadow: "0 0 20px rgba(0,229,255,0.3)"
  },
  logoText: {
    fontSize: "15px", fontWeight: 800, letterSpacing: "2px",
    background: "linear-gradient(90deg,#00e5ff,#fff)",
    WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent"
  },
  adminBadge: {
    fontSize: "10px", color: "#ffab00", letterSpacing: "2px",
    fontWeight: 700, marginLeft: "48px", marginTop: "-2px"
  },
  navItem: (active) => ({
    display: "flex", alignItems: "center", gap: "10px",
    padding: "11px 20px", cursor: "pointer",
    background: active ? "rgba(0,229,255,0.08)" : "transparent",
    borderLeft: active ? "3px solid #00e5ff" : "3px solid transparent",
    color: active ? "#00e5ff" : "#6a8099",
    fontSize: "14px", fontWeight: 600, letterSpacing: "0.5px",
    transition: "all 0.2s", userSelect: "none"
  }),
  navSection: {
    fontSize: "10px", color: "#334455", letterSpacing: "2px",
    fontWeight: 700, padding: "16px 20px 6px", textTransform: "uppercase"
  },
  sideBottom: {
    marginTop: "auto", padding: "16px 20px",
    borderTop: "1px solid #1a2438"
  },
  logoutBtn: {
    width: "100%", padding: "10px",
    background: "rgba(255,23,68,0.1)", border: "1px solid rgba(255,23,68,0.3)",
    borderRadius: "8px", color: "#ff1744", cursor: "pointer",
    fontWeight: 700, fontSize: "13px", letterSpacing: "1px"
  },
  // MAIN
  main: { flex: 1, overflowX: "hidden" },
  topbar: {
    display: "flex", alignItems: "center", justifyContent: "space-between",
    padding: "16px 28px", background: "#07090f",
    borderBottom: "1px solid #1a2438", position: "sticky", top: 0, zIndex: 100
  },
  pageTitle: {
    fontFamily: "'Figtree',sans-serif", fontSize: "16px",
    fontWeight: 700, letterSpacing: "2px", color: "#ddeeff"
  },
  content: { padding: "28px" },
  // CARDS
  statsGrid: {
    display: "grid", gridTemplateColumns: "repeat(4,1fr)",
    gap: "16px", marginBottom: "28px"
  },
  statCard: (color) => ({
    background: "#0c1220", border: `1px solid ${color}33`,
    borderRadius: "14px", padding: "22px",
    borderTop: `2px solid ${color}`
  }),
  statVal: (color) => ({
    fontFamily: "'Figtree',sans-serif", fontSize: "1.8rem",
    fontWeight: 700, color, display: "block"
  }),
  statLbl: {
    fontSize: "12px", color: "#6a8099", marginTop: "6px",
    letterSpacing: "0.5px"
  },
  // TABLE
  tableWrap: {
    background: "#0c1220", border: "1px solid #1a2438",
    borderRadius: "14px", overflow: "hidden", marginTop: "20px"
  },
  tableHead: {
    display: "flex", justifyContent: "space-between", alignItems: "center",
    padding: "16px 22px", borderBottom: "1px solid #1a2438"
  },
  tableTitle: {
    fontFamily: "'Figtree',sans-serif", fontSize: "13px",
    fontWeight: 700, letterSpacing: "1px"
  },
  th: {
    padding: "12px 16px", textAlign: "left",
    fontSize: "10px", fontWeight: 700, color: "#6a8099",
    letterSpacing: "1.5px", textTransform: "uppercase",
    borderBottom: "1px solid #1a2438", background: "#101828"
  },
  td: {
    padding: "13px 16px", fontSize: "13px",
    borderBottom: "1px solid #1a2438"
  },
  // BADGES
  badge: (color, bg) => ({
    padding: "3px 10px", borderRadius: "100px",
    fontSize: "10px", fontWeight: 700, letterSpacing: "1px",
    color, background: bg
  }),
  // BUTTONS
  btn: (color, bg) => ({
    padding: "7px 14px", border: `1px solid ${color}`,
    borderRadius: "7px", background: bg || "transparent",
    color, fontSize: "12px", fontWeight: 700,
    cursor: "pointer", letterSpacing: "0.5px", transition: "all 0.2s"
  }),
  btnPrimary: {
    padding: "10px 22px", border: "none", borderRadius: "8px",
    background: "linear-gradient(135deg,#00e5ff,#00b8d4)",
    color: "#000", fontSize: "13px", fontWeight: 700,
    cursor: "pointer", letterSpacing: "0.5px"
  },
  // FORM
  input: {
    width: "100%", padding: "11px 14px",
    background: "#101828", border: "1.5px solid #1a2438",
    borderRadius: "8px", color: "#ddeeff",
    fontSize: "13px", outline: "none", marginBottom: "14px"
  },
  label: {
    fontSize: "12px", fontWeight: 700, color: "#6a8099",
    letterSpacing: "0.5px", display: "block", marginBottom: "6px"
  },
  formCard: {
    background: "#0c1220", border: "1px solid #1a2438",
    borderRadius: "14px", padding: "24px", marginBottom: "20px"
  },
  formTitle: {
    fontFamily: "'Figtree',sans-serif", fontSize: "13px",
    fontWeight: 700, letterSpacing: "1px", marginBottom: "18px",
    color: "#00e5ff"
  },
  grid2: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" },
  // MODAL
  modalBg: {
    position: "fixed", inset: 0, background: "rgba(0,0,0,0.85)",
    backdropFilter: "blur(8px)", zIndex: 2000,
    display: "flex", alignItems: "center", justifyContent: "center"
  },
  modalBox: {
    background: "#0c1220", border: "1px solid #1a2438",
    borderRadius: "16px", padding: "32px",
    width: "100%", maxWidth: "480px", position: "relative"
  },
  // TOAST
  toast: (show) => ({
    position: "fixed", bottom: "24px", right: "24px",
    background: "#0c1220", border: "1px solid #00e5ff",
    borderRadius: "10px", padding: "12px 20px",
    color: "#00e5ff", fontSize: "13px", fontWeight: 700,
    zIndex: 3000, transform: show ? "translateY(0)" : "translateY(100px)",
    opacity: show ? 1 : 0, transition: "all 0.4s"
  })
};

// ── HELPERS ──────────────────────────────────────────────
const getAdminToken = () => localStorage.getItem("adminToken");
const adminHeaders = () => ({ Authorization: "Bearer " + getAdminToken() });

const fmt = (n) => "USDT " + Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtDate = (d) => new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });

const txLabel = (type, note) => {
  if (type === "level_income") {
    return (note && note.toLowerCase().includes("daily")) ? "Daily Income" : "Level Income";
  }
  const map = {
    deposit: "Deposit", withdraw: "Withdraw",
    referral_bonus: "Referral Bonus", manual_credit: "Manual Credit", manual_debit: "Manual Debit",
  };
  return map[type] || type;
};

const txColor = (type) => {
  const map = {
    deposit: ["#00e676", "rgba(0,230,118,0.1)"],
    withdraw: ["#ff1744", "rgba(255,23,68,0.1)"],
    referral_bonus: ["#ffab00", "rgba(255,171,0,0.1)"],
    level_income: ["#00e5ff", "rgba(0,229,255,0.1)"],
    manual_credit: ["#7c4dff", "rgba(124,77,255,0.1)"],
    manual_debit: ["#ff6d00", "rgba(255,109,0,0.1)"],
  };
  return map[type] || ["#6a8099", "rgba(106,128,153,0.1)"];
};

const statusColor = (s) => {
  const map = {
    pending: ["#ffab00", "rgba(255,171,0,0.1)"],
    approved: ["#00e676", "rgba(0,230,118,0.1)"],
    rejected: ["#ff1744", "rgba(255,23,68,0.1)"],
    completed: ["#00e676", "rgba(0,230,118,0.1)"],
  };
  return map[s] || ["#6a8099", "rgba(106,128,153,0.1)"];
};

// ── MAIN COMPONENT ───────────────────────────────────────
export default function Admin() {
  const [page, setPage] = useState("login");
  const [toast, setToast] = useState({ show: false, msg: "" });
  const [sideOpen, setSideOpen] = useState(false);
  const isMobile = useIsMobile();

  const showToast = (msg) => {
    setToast({ show: true, msg });
    setTimeout(() => setToast({ show: false, msg: "" }), 3000);
  };

  // Check if already logged in
  useEffect(() => {
    if (getAdminToken()) setPage("dashboard");
  }, []);

  const logout = () => {
    localStorage.removeItem("adminToken");
    setPage("login");
  };

  // ── NAVIGATION ITEMS ──
  const navItems = [
    { id: "dashboard", icon: "📊", label: "Dashboard" },
    { id: "users", icon: "👥", label: "Users" },
    { id: "withdrawals", icon: "💸", label: "Withdrawals" },
    { id: "transactions", icon: "📋", label: "Transactions" },
    { id: "notices", icon: "📢", label: "Notices" },
    { id: "settings", icon: "⚙️", label: "Settings" },
  ];

  if (page === "login") return <LoginPage onLogin={() => setPage("dashboard")} showToast={showToast} toast={toast} />;

  const navGo = (id) => { setPage(id); setSideOpen(false); };

  return (
    <div style={S.wrap}>
      {/* Mobile overlay */}
      {isMobile && sideOpen && (
        <div onClick={() => setSideOpen(false)} style={{
          position:"fixed", inset:0, background:"rgba(0,0,0,0.6)",
          zIndex:999, backdropFilter:"blur(4px)"
        }} />
      )}

      {/* SIDEBAR */}
      <div style={{
        ...S.sidebar,
        ...(isMobile ? {
          position:"fixed", top:0, left:0, height:"100vh", zIndex:1000,
          transform: sideOpen ? "translateX(0)" : "translateX(-100%)",
          transition:"transform 0.28s cubic-bezier(.4,0,.2,1)",
        } : {})
      }}>
        <div style={S.sideTop}>
          <div style={S.logo}>
            <div style={S.logoMark}>E₿</div>
            <span style={S.logoText}>EARNIX</span>
          </div>
          <div style={S.adminBadge}>ADMIN PANEL</div>
        </div>

        <div style={{ padding: "12px 0" }}>
          <div style={S.navSection}>Main</div>
          {navItems.map(n => (
            <div key={n.id} style={S.navItem(page === n.id)} onClick={() => navGo(n.id)}>
              <span>{n.icon}</span>
              <span>{n.label}</span>
            </div>
          ))}
        </div>

        <div style={S.sideBottom}>
          <div style={{ fontSize: "11px", color: "#334455", marginBottom: "10px", textAlign: "center" }}>
            admin@earnixglobal.com
          </div>
          <button style={S.logoutBtn} onClick={logout}>⏻ Logout</button>
        </div>
      </div>

      {/* MAIN */}
      <div style={{ ...S.main, marginLeft: isMobile ? 0 : undefined }}>
        <div style={S.topbar}>
          <div style={{ display:"flex", alignItems:"center", gap:12 }}>
            {isMobile && (
              <button onClick={() => setSideOpen(o => !o)} style={{
                background:"none", border:"1px solid #1a2438", borderRadius:8,
                color:"#ddeeff", cursor:"pointer", padding:"6px 10px", fontSize:18,
              }}>☰</button>
            )}
            <div style={S.pageTitle}>{navItems.find(n => n.id === page)?.icon} {navItems.find(n => n.id === page)?.label?.toUpperCase()}</div>
          </div>
          <div style={{ fontSize: "12px", color: "#6a8099" }}>
            {new Date().toLocaleDateString("en-IN", { weekday: "long", year: "numeric", month: "long", day: "numeric" })}
          </div>
        </div>

        <div style={S.content}>
          {page === "dashboard" && <DashboardPage showToast={showToast} />}
          {page === "users" && <UsersPage showToast={showToast} />}
          {page === "withdrawals" && <WithdrawalsPage showToast={showToast} />}
          {page === "transactions" && <TransactionsPage />}
          {page === "notices" && <NoticesPage showToast={showToast} />}
          {page === "settings" && <SettingsPage showToast={showToast} />}
        </div>
      </div>

      {/* TOAST */}
      <div style={S.toast(toast.show)}>{toast.msg}</div>
    </div>
  );
}


// ── LOGIN PAGE ────────────────────────────────────────────
function LoginPage({ onLogin, showToast }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [otp, setOtp] = useState("");
  const [step, setStep] = useState(1); // 1 = password, 2 = OTP
  const [loading, setLoading] = useState(false);

  // Step 1: submit email + password → server emails an OTP
  const login = async () => {
    if (!email || !password) { showToast("⚠ Fill all fields"); return; }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/admin/login`, { email, password });
      if (res.data.step === "otp") {
        setStep(2);
        showToast("📧 OTP sent to admin email");
      } else if (res.data.token) {
        localStorage.setItem("adminToken", res.data.token);
        showToast("✅ Admin login successful!");
        onLogin();
      } else {
        showToast("❌ " + (res.data.message || "Login failed"));
      }
    } catch (e) {
      showToast("❌ " + (e.response?.data?.message || "Invalid admin credentials"));
    }
    setLoading(false);
  };

  // Step 2: submit OTP → server returns admin token
  const verifyOtp = async () => {
    if (!otp || otp.length !== 6) { showToast("⚠ Enter the 6-digit OTP"); return; }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/admin/login/verify`, { email, otp });
      if (res.data.token) {
        localStorage.setItem("adminToken", res.data.token);
        showToast("✅ Admin login successful!");
        onLogin();
      } else {
        showToast("❌ " + (res.data.message || "Verification failed"));
      }
    } catch (e) {
      showToast("❌ " + (e.response?.data?.message || "Invalid OTP"));
    }
    setLoading(false);
  };

  return (
    <div style={{
      minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center",
      background: "#03050a",
      backgroundImage: "linear-gradient(rgba(0,229,255,0.02) 1px,transparent 1px),linear-gradient(90deg,rgba(0,229,255,0.02) 1px,transparent 1px)",
      backgroundSize: "44px 44px"
    }}>
      <div style={{
        background: "#0c1220", border: "1px solid #1a2438",
        borderRadius: "20px", padding: "44px 40px",
        width: "100%", maxWidth: "400px",
        boxShadow: "0 40px 80px rgba(0,0,0,0.6)"
      }}>
        <div style={{ textAlign: "center", marginBottom: "32px" }}>
          <div style={{ ...S.logoMark, margin: "0 auto 12px", width: "52px", height: "52px", fontSize: "18px" }}>E₿</div>
          <div style={{ fontFamily: "'Figtree',sans-serif", fontSize: "18px", fontWeight: 800, letterSpacing: "3px", background: "linear-gradient(90deg,#00e5ff,#fff)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>EARNIX GLOBAL</div>
          <div style={{ fontSize: "11px", color: "#ffab00", letterSpacing: "2px", marginTop: "4px", fontWeight: 700 }}>ADMIN PANEL</div>
        </div>

        {step === 1 ? (
          <>
            <label style={S.label}>Admin Email</label>
            <input style={S.input} type="email" placeholder="admin@earnixglobal.com"
              value={email} onChange={e => setEmail(e.target.value)} />

            <label style={S.label}>Password</label>
            <input style={S.input} type="password" placeholder="••••••••"
              value={password} onChange={e => setPassword(e.target.value)}
              onKeyDown={e => e.key === "Enter" && login()} />

            <button style={{ ...S.btnPrimary, width: "100%", padding: "13px", marginTop: "6px", fontSize: "14px" }}
              onClick={login} disabled={loading}>
              {loading ? "Verifying..." : "Continue →"}
            </button>
          </>
        ) : (
          <>
            <div style={{ textAlign: "center", marginBottom: "16px", fontSize: "13px", color: "#8aa0c0" }}>
              A 6-digit code was sent to the admin email. Enter it below.
            </div>
            <label style={S.label}>Verification Code (OTP)</label>
            <input style={{ ...S.input, letterSpacing: "6px", textAlign: "center", fontSize: "18px" }}
              type="text" inputMode="numeric" maxLength={6} placeholder="------"
              value={otp} onChange={e => setOtp(e.target.value.replace(/\D/g, ""))}
              onKeyDown={e => e.key === "Enter" && verifyOtp()} autoFocus />

            <button style={{ ...S.btnPrimary, width: "100%", padding: "13px", marginTop: "6px", fontSize: "14px" }}
              onClick={verifyOtp} disabled={loading}>
              {loading ? "Verifying..." : "Verify & Login →"}
            </button>
            <button style={{ background: "none", border: "none", color: "#8aa0c0", fontSize: "12px", marginTop: "14px", cursor: "pointer", width: "100%" }}
              onClick={() => { setStep(1); setOtp(""); }}>
              ← Back
            </button>
          </>
        )}
      </div>
    </div>
  );
}


// ── DASHBOARD PAGE ────────────────────────────────────────
function DashboardPage({ showToast }) {
  const [stats, setStats] = useState({});
  const [recentWd, setRecentWd] = useState([]);
  const [recentTx, setRecentTx] = useState([]);

  useEffect(() => {
    fetchStats();
    fetchRecent();
  }, []);

  const fetchStats = async () => {
    try {
      const res = await axios.get(`${API}/admin/stats`, { headers: adminHeaders() });
      setStats(res.data);
    } catch { showToast("❌ Failed to load stats"); }
  };

  const fetchRecent = async () => {
    try {
      const [wd, tx] = await Promise.all([
        axios.get(`${API}/admin/withdrawals?status=pending`, { headers: adminHeaders() }),
        axios.get(`${API}/admin/transactions`, { headers: adminHeaders() })
      ]);
      setRecentWd(wd.data.data?.slice(0, 5) || []);
      setRecentTx(tx.data.data?.slice(0, 8) || []);
    } catch {}
  };

  const statCards = [
    { label: "Total Users", value: stats.totalUsers || 0, color: "#00e5ff", icon: "👥" },
    { label: "Total Deposit", value: fmt(stats.totalDeposit), color: "#00e676", icon: "💰" },
    { label: "Total Withdraw", value: fmt(stats.totalWithdraw), color: "#ff1744", icon: "💸" },
    { label: "Pending Withdrawals", value: stats.pendingWithdraw || 0, color: "#ffab00", icon: "⏳" },
    { label: "Active Users", value: stats.activeUsers || 0, color: "#7c4dff", icon: "✅" },
    { label: "Blocked Users", value: stats.blockedUsers || 0, color: "#ff6d00", icon: "🚫" },
    { label: "Total Transactions", value: stats.totalTransactions || 0, color: "#448aff", icon: "📋" },
    { label: "Net Balance", value: fmt((stats.totalDeposit || 0) - (stats.totalWithdraw || 0)), color: "#00e5ff", icon: "📊" },
  ];

  return (
    <div>
      {/* STATS */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: "14px", marginBottom: "24px" }}>
        {statCards.map((s, i) => (
          <div key={i} style={S.statCard(s.color)}>
            <div style={{ fontSize: "20px", marginBottom: "8px" }}>{s.icon}</div>
            <div style={S.statVal(s.color)}>{s.value}</div>
            <div style={S.statLbl}>{s.label}</div>
          </div>
        ))}
      </div>

      <div style={S.grid2}>
        {/* PENDING WITHDRAWALS */}
        <div style={S.tableWrap}>
          <div style={S.tableHead}>
            <div style={S.tableTitle}>⏳ Pending Withdrawals</div>
            <span style={S.badge("#ffab00", "rgba(255,171,0,0.1)")}>{recentWd.length}</span>
          </div>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead><tr>
              {["User", "Amount", "Coin", "Wallet Address", "Date"].map(h => <th key={h} style={S.th}>{h}</th>)}
            </tr></thead>
            <tbody>
              {recentWd.length === 0
                ? <tr><td colSpan={5} style={{ ...S.td, textAlign: "center", color: "#334455" }}>No pending withdrawals</td></tr>
                : recentWd.map((w, i) => (
                  <tr key={i}>
                    <td style={S.td}><div style={{ fontSize: "12px" }}>{w.userEmail}</div></td>
                    <td style={{ ...S.td, color: "#ff1744", fontWeight: 700 }}>{fmt(w.amount)}</td>
                    <td style={S.td}><span style={S.badge("#00e5ff", "rgba(0,229,255,0.1)")}>{w.coin || "USDT"}</span></td>
                    <td style={{ ...S.td, fontSize: "11px", color: "#6a8099" }}>
                      <div style={{ wordBreak: "break-all", maxWidth: "180px" }}>{w.walletAddress || "—"}</div>
                    </td>
                    <td style={{ ...S.td, fontSize: "11px", color: "#6a8099" }}>{fmtDate(w.requestDate)}</td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>

        {/* RECENT TX */}
        <div style={S.tableWrap}>
          <div style={S.tableHead}>
            <div style={S.tableTitle}>📋 Recent Transactions</div>
          </div>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead><tr>
              {["User", "Type", "Amount"].map(h => <th key={h} style={S.th}>{h}</th>)}
            </tr></thead>
            <tbody>
              {recentTx.map((t, i) => {
                const [c, bg] = txColor(t.type);
                return (
                  <tr key={i}>
                    <td style={{ ...S.td, fontSize: "11px" }}>{t.userId?.email || "—"}</td>
                    <td style={S.td}><span style={S.badge(c, bg)}>{txLabel(t.type, t.note)}</span></td>
                    <td style={{ ...S.td, color: c, fontWeight: 700 }}>{fmt(t.amount)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}


// ── USERS PAGE ────────────────────────────────────────────
function UsersPage({ showToast }) {
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const [balModal, setBalModal] = useState(false);
  const [balType, setBalType] = useState("credit");
  const [balAmt, setBalAmt] = useState("");
  const [balNote, setBalNote] = useState("");
  const [userDetail, setUserDetail] = useState(null);

  useEffect(() => { fetchUsers(); }, [search]);

  const fetchUsers = async () => {
    try {
      const res = await axios.get(`${API}/admin/users?search=${search}`, { headers: adminHeaders() });
      setUsers(res.data.users || []);
    } catch { showToast("❌ Failed to load users"); }
  };

  const toggleBlock = async (id) => {
    try {
      const res = await axios.put(`${API}/admin/user/${id}/block`, {}, { headers: adminHeaders() });
      showToast(res.data.isBlocked ? "🚫 User blocked" : "✅ User unblocked");
      fetchUsers();
    } catch { showToast("❌ Error"); }
  };

  const adjustBalance = async () => {
    if (!balAmt) { showToast("⚠ Enter amount"); return; }
    try {
      const res = await axios.put(`${API}/admin/user/${selected._id}/balance`,
        { amount: balAmt, type: balType, note: balNote },
        { headers: adminHeaders() }
      );
      showToast("✅ " + res.data.message);
      setBalModal(false); setBalAmt(""); setBalNote("");
      fetchUsers();
    } catch { showToast("❌ Error"); }
  };

  const viewDetail = async (id) => {
    try {
      const res = await axios.get(`${API}/admin/user/${id}`, { headers: adminHeaders() });
      setUserDetail(res.data);
    } catch { showToast("❌ Error"); }
  };

  return (
    <div>
      {/* SEARCH */}
      <div style={{ display: "flex", gap: "12px", marginBottom: "20px" }}>
        <input style={{ ...S.input, marginBottom: 0, flex: 1 }}
          placeholder="🔍 Search by email or name..."
          value={search} onChange={e => setSearch(e.target.value)} />
        <button style={S.btnPrimary} onClick={fetchUsers}>Search</button>
      </div>

      {/* TABLE */}
      <div style={S.tableWrap}>
        <div style={S.tableHead}>
          <div style={S.tableTitle}>👥 All Users ({users.length})</div>
        </div>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead><tr>
              {["Name", "Email", "Balance", "Total Deposit", "Total Earn", "Plan", "Status", "Joined", "Actions"].map(h =>
                <th key={h} style={S.th}>{h}</th>
              )}
            </tr></thead>
            <tbody>
              {users.map((u, i) => (
                <tr key={i} style={{ background: i % 2 === 0 ? "transparent" : "rgba(255,255,255,0.01)" }}>
                  <td style={S.td}>{u.name || "—"}</td>
                  <td style={{ ...S.td, fontSize: "12px", color: "#00e5ff" }}>{u.email}</td>
                  <td style={{ ...S.td, color: "#00e676", fontWeight: 700 }}>{fmt(u.balance)}</td>
                  <td style={S.td}>{fmt(u.totalDeposit)}</td>
                  <td style={S.td}>{fmt(u.totalEarning)}</td>
                  <td style={S.td}><span style={S.badge("#7c4dff", "rgba(124,77,255,0.1)")}>{u.plan?.toUpperCase()}</span></td>
                  <td style={S.td}>
                    <span style={S.badge(...(u.isBlocked ? ["#ff1744", "rgba(255,23,68,0.1)"] : ["#00e676", "rgba(0,230,118,0.1)"]))}>
                      {u.isBlocked ? "BLOCKED" : "ACTIVE"}
                    </span>
                  </td>
                  <td style={{ ...S.td, fontSize: "11px", color: "#6a8099" }}>{fmtDate(u.joinDate)}</td>
                  <td style={S.td}>
                    <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
                      <button style={S.btn("#00e5ff")} onClick={() => viewDetail(u._id)}>👁 View</button>
                      <button style={S.btn("#ffab00")} onClick={() => { setSelected(u); setBalModal(true); }}>💰 Balance</button>
                      <button style={S.btn(u.isBlocked ? "#00e676" : "#ff1744")} onClick={() => toggleBlock(u._id)}>
                        {u.isBlocked ? "✅ Unblock" : "🚫 Block"}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {users.length === 0 && <tr><td colSpan={9} style={{ ...S.td, textAlign: "center", color: "#334455" }}>No users found</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {/* BALANCE MODAL */}
      {balModal && selected && (
        <div style={S.modalBg} onClick={() => setBalModal(false)}>
          <div style={S.modalBox} onClick={e => e.stopPropagation()}>
            <div style={{ fontFamily: "'Figtree',sans-serif", fontSize: "14px", fontWeight: 700, marginBottom: "4px", color: "#00e5ff" }}>
              💰 Adjust Balance
            </div>
            <div style={{ fontSize: "13px", color: "#6a8099", marginBottom: "20px" }}>{selected.email} — Current: {fmt(selected.balance)}</div>

            <label style={S.label}>Type</label>
            <select style={{ ...S.input }} value={balType} onChange={e => setBalType(e.target.value)}>
              <option value="credit">✅ Credit (Add)</option>
              <option value="debit">❌ Debit (Remove)</option>
            </select>

            <label style={S.label}>Amount (USDT)</label>
            <input style={S.input} type="number" placeholder="Enter amount"
              value={balAmt} onChange={e => setBalAmt(e.target.value)} />

            <label style={S.label}>Note (Optional)</label>
            <input style={S.input} placeholder="Reason..." value={balNote} onChange={e => setBalNote(e.target.value)} />

            <div style={{ display: "flex", gap: "10px" }}>
              <button style={S.btnPrimary} onClick={adjustBalance}>Confirm</button>
              <button style={S.btn("#6a8099")} onClick={() => setBalModal(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* USER DETAIL MODAL */}
      {userDetail && (
        <div style={S.modalBg} onClick={() => setUserDetail(null)}>
          <div style={{ ...S.modalBox, maxWidth: "680px", maxHeight: "80vh", overflowY: "auto" }} onClick={e => e.stopPropagation()}>
            <div style={{ fontFamily: "'Figtree',sans-serif", fontSize: "14px", fontWeight: 700, color: "#00e5ff", marginBottom: "16px" }}>
              👁 User Detail
            </div>
            <div style={S.grid2}>
              {[
                ["Name", userDetail.user?.name],
                ["Email", userDetail.user?.email],
                ["Phone", userDetail.user?.phone],
                ["Balance", fmt(userDetail.user?.balance)],
                ["Total Deposit", fmt(userDetail.user?.totalDeposit)],
                ["Total Withdraw", fmt(userDetail.user?.totalWithdraw)],
                ["Total Earning", fmt(userDetail.user?.totalEarning)],
                ["Referral Code", userDetail.user?.referralCode],
                ["Referred By", userDetail.user?.referredBy || "Direct"],
                ["Plan", userDetail.user?.plan],
                ["Status", userDetail.user?.isBlocked ? "BLOCKED" : "ACTIVE"],
                ["Joined", fmtDate(userDetail.user?.joinDate)],
              ].map(([k, v], i) => (
                <div key={i} style={{ background: "#101828", borderRadius: "8px", padding: "12px" }}>
                  <div style={{ fontSize: "10px", color: "#6a8099", letterSpacing: "1px", marginBottom: "4px" }}>{k}</div>
                  <div style={{ fontSize: "13px", fontWeight: 700 }}>{v || "—"}</div>
                </div>
              ))}
            </div>

            <div style={{ marginTop: "20px", fontFamily: "'Figtree',sans-serif", fontSize: "12px", fontWeight: 700, color: "#ffab00", marginBottom: "10px" }}>
              Recent Transactions
            </div>
            {userDetail.transactions?.slice(0, 5).map((t, i) => {
              const [c, bg] = txColor(t.type);
              return (
                <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "10px", background: "#101828", borderRadius: "8px", marginBottom: "6px" }}>
                  <span style={S.badge(c, bg)}>{txLabel(t.type, t.note)}</span>
                  <span style={{ color: c, fontWeight: 700, fontSize: "13px" }}>{fmt(t.amount)}</span>
                  <span style={{ fontSize: "11px", color: "#6a8099" }}>{fmtDate(t.date)}</span>
                </div>
              );
            })}
            <button style={{ ...S.btn("#6a8099"), marginTop: "16px" }} onClick={() => setUserDetail(null)}>Close</button>
          </div>
        </div>
      )}
    </div>
  );
}


// ── WITHDRAWALS PAGE ──────────────────────────────────────
function WithdrawalsPage({ showToast }) {
  const [data, setData] = useState([]);
  const [filter, setFilter] = useState("pending");
  const [note, setNote] = useState("");
  const [noteModal, setNoteModal] = useState(null);

  useEffect(() => { fetchWd(); }, [filter]);

  const fetchWd = async () => {
    try {
      const res = await axios.get(`${API}/admin/withdrawals?status=${filter}`, { headers: adminHeaders() });
      setData(res.data.data || []);
    } catch { showToast("❌ Failed to load"); }
  };

  const process = async (id, action) => {
    try {
      const res = await axios.put(`${API}/admin/withdraw/${id}/${action}`, { note }, { headers: adminHeaders() });
      showToast(action === "approve" ? "✅ Approved!" : "❌ Rejected & Refunded");
      setNoteModal(null); setNote("");
      fetchWd();
    } catch { showToast("❌ Error"); }
  };

  return (
    <div>
      {/* FILTERS */}
      <div style={{ display: "flex", gap: "8px", marginBottom: "20px" }}>
        {["pending", "approved", "rejected"].map(f => (
          <button key={f} style={{
            ...S.btn(filter === f ? "#000" : "#6a8099", filter === f ? "#00e5ff" : "transparent"),
            border: filter === f ? "none" : "1px solid #1a2438"
          }} onClick={() => setFilter(f)}>
            {f === "pending" ? "⏳" : f === "approved" ? "✅" : "❌"} {f.toUpperCase()}
          </button>
        ))}
      </div>

      <div style={S.tableWrap}>
        <div style={S.tableHead}>
          <div style={S.tableTitle}>💸 Withdrawal Requests</div>
          <span style={S.badge("#ffab00", "rgba(255,171,0,0.1)")}>{data.length} records</span>
        </div>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead><tr>
              {["User", "Amount", "Fee", "Final", "Coin", "Wallet", "Status", "Date", "Action"].map(h =>
                <th key={h} style={S.th}>{h}</th>
              )}
            </tr></thead>
            <tbody>
              {data.map((w, i) => {
                const [sc, sbg] = statusColor(w.status);
                return (
                  <tr key={i}>
                    <td style={{ ...S.td, fontSize: "12px" }}>{w.userEmail}</td>
                    <td style={{ ...S.td, color: "#ff1744", fontWeight: 700 }}>{fmt(w.amount)}</td>
                    <td style={{ ...S.td, color: "#ffab00" }}>{fmt(w.fee)}</td>
                    <td style={{ ...S.td, color: "#00e676", fontWeight: 700 }}>{fmt(w.finalAmount)}</td>
                    <td style={S.td}><span style={S.badge("#00e5ff", "rgba(0,229,255,0.1)")}>{w.coin || "USDT"}</span></td>
                    <td style={{ ...S.td, fontSize: "11px", color: "#6a8099" }}>
                      <div style={{ wordBreak: "break-all", maxWidth: "180px" }}>{w.walletAddress}</div>
                    </td>
                    <td style={S.td}><span style={S.badge(sc, sbg)}>{w.status.toUpperCase()}</span></td>
                    <td style={{ ...S.td, fontSize: "11px", color: "#6a8099" }}>{fmtDate(w.requestDate)}</td>
                    <td style={S.td}>
                      {w.status === "pending" && (
                        <div style={{ display: "flex", gap: "6px" }}>
                          <button style={S.btn("#00e676")} onClick={() => setNoteModal({ id: w._id, action: "approve" })}>✅ Approve</button>
                          <button style={S.btn("#ff1744")} onClick={() => setNoteModal({ id: w._id, action: "reject" })}>❌ Reject</button>
                        </div>
                      )}
                      {w.status !== "pending" && <span style={{ fontSize: "11px", color: "#334455" }}>{w.adminNote || "—"}</span>}
                    </td>
                  </tr>
                );
              })}
              {data.length === 0 && <tr><td colSpan={9} style={{ ...S.td, textAlign: "center", color: "#334455" }}>No records</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {/* NOTE MODAL */}
      {noteModal && (
        <div style={S.modalBg} onClick={() => setNoteModal(null)}>
          <div style={S.modalBox} onClick={e => e.stopPropagation()}>
            <div style={{ fontFamily: "'Figtree',sans-serif", fontSize: "14px", fontWeight: 700, color: noteModal.action === "approve" ? "#00e676" : "#ff1744", marginBottom: "16px" }}>
              {noteModal.action === "approve" ? "✅ Approve Withdrawal" : "❌ Reject Withdrawal"}
            </div>
            <label style={S.label}>Admin Note (Optional)</label>
            <input style={S.input} placeholder="e.g. Payment sent via USDT TRC20"
              value={note} onChange={e => setNote(e.target.value)} />
            <div style={{ display: "flex", gap: "10px" }}>
              <button style={{ ...S.btnPrimary, background: noteModal.action === "approve" ? "linear-gradient(135deg,#00e676,#00b248)" : "linear-gradient(135deg,#ff1744,#c62828)" }}
                onClick={() => process(noteModal.id, noteModal.action)}>
                Confirm {noteModal.action}
              </button>
              <button style={S.btn("#6a8099")} onClick={() => setNoteModal(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


// ── TRANSACTIONS PAGE ─────────────────────────────────────
function TransactionsPage() {
  const [data, setData] = useState([]);
  const [filter, setFilter] = useState("");
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);

  useEffect(() => { fetchTx(); }, [filter, page]);

  const fetchTx = async () => {
    try {
      const res = await axios.get(`${API}/admin/transactions?type=${filter}&page=${page}`, { headers: adminHeaders() });
      setData(res.data.data || []);
      setTotal(res.data.total || 0);
    } catch {}
  };

  const types = ["", "deposit", "withdraw", "referral_bonus", "level_income", "manual_credit", "manual_debit"];

  return (
    <div>
      <div style={{ display: "flex", gap: "8px", marginBottom: "20px", flexWrap: "wrap" }}>
        {types.map(t => (
          <button key={t} style={{
            ...S.btn(filter === t ? "#000" : "#6a8099", filter === t ? "#00e5ff" : "transparent"),
            border: filter === t ? "none" : "1px solid #1a2438", fontSize: "11px"
          }} onClick={() => { setFilter(t); setPage(1); }}>
            {t || "ALL"}
          </button>
        ))}
      </div>

      <div style={S.tableWrap}>
        <div style={S.tableHead}>
          <div style={S.tableTitle}>📋 Transactions ({total})</div>
          <div style={{ display: "flex", gap: "8px" }}>
            <button style={S.btn("#6a8099")} onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>◀ Prev</button>
            <span style={{ padding: "7px 12px", fontSize: "12px", color: "#6a8099" }}>Page {page}</span>
            <button style={S.btn("#6a8099")} onClick={() => setPage(p => p + 1)} disabled={data.length < 20}>Next ▶</button>
          </div>
        </div>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead><tr>
              {["User", "Type", "Amount", "Fee", "Note", "Status", "Date"].map(h => <th key={h} style={S.th}>{h}</th>)}
            </tr></thead>
            <tbody>
              {data.map((t, i) => {
                const [c, bg] = txColor(t.type);
                const [sc, sbg] = statusColor(t.status);
                return (
                  <tr key={i}>
                    <td style={{ ...S.td, fontSize: "11px" }}>{t.userId?.email || "—"}</td>
                    <td style={S.td}><span style={S.badge(c, bg)}>{txLabel(t.type, t.note)}</span></td>
                    <td style={{ ...S.td, color: c, fontWeight: 700 }}>{fmt(t.amount)}</td>
                    <td style={{ ...S.td, color: "#ffab00" }}>{t.fee ? fmt(t.fee) : "—"}</td>
                    <td style={{ ...S.td, fontSize: "11px", color: "#6a8099" }}>{t.note || "—"}</td>
                    <td style={S.td}><span style={S.badge(sc, sbg)}>{t.status}</span></td>
                    <td style={{ ...S.td, fontSize: "11px", color: "#6a8099" }}>{fmtDate(t.date)}</td>
                  </tr>
                );
              })}
              {data.length === 0 && <tr><td colSpan={7} style={{ ...S.td, textAlign: "center", color: "#334455" }}>No transactions</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}


// ── NOTICES PAGE ──────────────────────────────────────────
function NoticesPage({ showToast }) {
  const [notices, setNotices] = useState([]);
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const [type, setType] = useState("info");

  useEffect(() => { fetchNotices(); }, []);

  const fetchNotices = async () => {
    try {
      const res = await axios.get(`${API}/admin/notices`, { headers: adminHeaders() });
      setNotices(res.data);
    } catch {}
  };

  const addNotice = async () => {
    if (!title || !message) { showToast("⚠ Fill all fields"); return; }
    try {
      await axios.post(`${API}/admin/notice`, { title, message, type }, { headers: adminHeaders() });
      showToast("✅ Notice created!");
      setTitle(""); setMessage("");
      fetchNotices();
    } catch { showToast("❌ Error"); }
  };

  const deleteNotice = async (id) => {
    try {
      await axios.delete(`${API}/admin/notice/${id}`, { headers: adminHeaders() });
      showToast("🗑 Notice deleted");
      fetchNotices();
    } catch { showToast("❌ Error"); }
  };

  const noticeColors = { info: "#448aff", warning: "#ffab00", success: "#00e676", danger: "#ff1744" };

  return (
    <div style={S.grid2}>
      {/* ADD NOTICE */}
      <div style={S.formCard}>
        <div style={S.formTitle}>📢 Create Notice</div>
        <label style={S.label}>Title</label>
        <input style={S.input} placeholder="Notice title" value={title} onChange={e => setTitle(e.target.value)} />
        <label style={S.label}>Message</label>
        <textarea style={{ ...S.input, minHeight: "100px", resize: "vertical" }}
          placeholder="Notice message..." value={message} onChange={e => setMessage(e.target.value)} />
        <label style={S.label}>Type</label>
        <select style={S.input} value={type} onChange={e => setType(e.target.value)}>
          <option value="info">ℹ Info</option>
          <option value="warning">⚠ Warning</option>
          <option value="success">✅ Success</option>
          <option value="danger">🚨 Danger</option>
        </select>
        <button style={S.btnPrimary} onClick={addNotice}>Post Notice</button>
      </div>

      {/* NOTICES LIST */}
      <div>
        {notices.map((n, i) => (
          <div key={i} style={{
            background: "#0c1220", border: `1px solid ${noticeColors[n.type]}44`,
            borderLeft: `3px solid ${noticeColors[n.type]}`,
            borderRadius: "10px", padding: "16px", marginBottom: "12px"
          }}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "6px" }}>
              <div style={{ fontWeight: 700, fontSize: "14px" }}>{n.title}</div>
              <button style={S.btn("#ff1744")} onClick={() => deleteNotice(n._id)}>🗑</button>
            </div>
            <div style={{ fontSize: "13px", color: "#6a8099", marginBottom: "6px" }}>{n.message}</div>
            <div style={{ fontSize: "11px", color: "#334455" }}>{fmtDate(n.date)}</div>
          </div>
        ))}
        {notices.length === 0 && <div style={{ color: "#334455", textAlign: "center", padding: "40px" }}>No notices yet</div>}
      </div>
    </div>
  );
}


// ── SETTINGS PAGE ─────────────────────────────────────────
function SettingsPage({ showToast }) {
  const defaults = {
    siteName: "Earnix Global",
    minDeposit: 5,
    minWithdraw: 10,
    withdrawFee: 5,
    instantBonus: 10,
    maintenanceMode: "false",
  };

  const [settings, setSettings] = useState(defaults);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/admin/settings`, { headers: adminHeaders() })
      .then(res => {
        const loaded = { ...defaults };
        (res.data || []).forEach(s => { loaded[s.key] = s.value; });
        setSettings(loaded);
      })
      .catch(() => showToast("⚠ Could not load settings"))
      .finally(() => setLoading(false));
  }, []);

  const saveAll = async () => {
    setSaving(true);
    try {
      await Promise.all(
        Object.entries(settings).map(([key, value]) =>
          axios.put(`${API}/admin/settings`, { key, value }, { headers: adminHeaders() })
        )
      );
      showToast("✅ All settings saved!");
    } catch {
      showToast("❌ Failed to save settings");
    } finally { setSaving(false); }
  };

  const saveOne = async (key, value) => {
    const updated = { ...settings, [key]: value };
    setSettings(updated);
    try {
      await axios.put(`${API}/admin/settings`, { key, value }, { headers: adminHeaders() });
      showToast(`✅ ${key} updated!`);
    } catch { showToast("❌ Save failed"); }
  };

  const fields = [
    { key: "siteName",     label: "Site Name",                   type: "text"   },
    { key: "minDeposit",   label: "Min Deposit (USDT)",           type: "number" },
    { key: "minWithdraw",  label: "Min Withdraw (USDT)",          type: "number" },
    { key: "withdrawFee",  label: "Withdraw Fee (%)",             type: "number" },
    { key: "instantBonus", label: "Instant Referral Bonus (%)",   type: "number" },
  ];

  if (loading) return <div style={{ color: "#6a8099", padding: "40px", textAlign: "center" }}>Loading settings...</div>;

  return (
    <div style={S.grid2}>
      <div style={S.formCard}>
        <div style={S.formTitle}>⚙️ Platform Settings</div>
        {fields.map(f => (
          <div key={f.key}>
            <label style={S.label}>{f.label}</label>
            <input style={S.input} type={f.type}
              value={settings[f.key] ?? ""}
              onChange={e => setSettings(prev => ({ ...prev, [f.key]: e.target.value }))} />
          </div>
        ))}
        <button style={{ ...S.btnPrimary, opacity: saving ? 0.6 : 1 }} onClick={saveAll} disabled={saving}>
          {saving ? "Saving..." : "💾 Save All Settings"}
        </button>
      </div>

      <div style={S.formCard}>
        <div style={S.formTitle}>🔧 Quick Controls</div>

        <div style={{ background: "#101828", borderRadius: "10px", padding: "16px", marginBottom: "14px" }}>
          <div style={{ fontWeight: 700, marginBottom: "6px" }}>Maintenance Mode</div>
          <div style={{ fontSize: "13px", color: "#6a8099", marginBottom: "12px" }}>Site users ke liye temporarily band karo</div>
          <button style={{
            ...S.btn(settings.maintenanceMode === "true" || settings.maintenanceMode === true ? "#00e676" : "#ff1744"),
            padding: "10px 20px"
          }} onClick={() => {
            const next = !(settings.maintenanceMode === "true" || settings.maintenanceMode === true);
            saveOne("maintenanceMode", String(next));
          }}>
            {settings.maintenanceMode === "true" || settings.maintenanceMode === true
              ? "✅ Turn OFF Maintenance"
              : "🔴 Enable Maintenance"}
          </button>
        </div>

        <div style={{ background: "#101828", borderRadius: "10px", padding: "16px", marginBottom: "14px" }}>
          <div style={{ fontWeight: 700, marginBottom: "6px" }}>Admin Credentials</div>
          <div style={{ fontSize: "12px", color: "#6a8099", marginBottom: "8px" }}>server.js mein change karo:</div>
          <div style={{ background: "#03050a", padding: "12px", borderRadius: "8px", fontFamily: "monospace", fontSize: "12px", color: "#00e5ff" }}>
            <div>Email: admin@earnixglobal.com</div>
            <div>Pass: Earnix@Admin2025</div>
          </div>
        </div>

        <div style={{ background: "#101828", borderRadius: "10px", padding: "16px" }}>
          <div style={{ fontWeight: 700, marginBottom: "6px" }}>Commission Structure (fixed in server.js)</div>
          {[
            ["⚡ L1 Instant Bonus", "10% on deposit"],
            ["L2 Deposit Income", "5%"],
            ["L3 Deposit Income", "3%"],
            ["Investment L1 Daily", "0.2%/day"],
            ["Investment L2 Daily", "0.15%/day"],
            ["Investment L3-L10",   "0.1% – 0.03%/day"],
          ].map(([l, v], i) => (
            <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "7px 0", borderBottom: "1px solid #1a2438", fontSize: "12px" }}>
              <span style={{ color: "#6a8099" }}>{l}</span>
              <span style={{ color: "#00e5ff", fontWeight: 700 }}>{v}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
